import { useState, type ReactNode } from 'react'

import { Modal } from '../Modal'

/**
 * Shared presentational shell for the two AWS-import IAM setup helpers
 * (per-account and organization). Renders a trigger link that opens a modal
 * with one tab per provisioning tool (CloudFormation / Terraform / CLI /
 * CloudShell) and a copy button. Each variant supplies its own snippet set;
 * this component owns only the open/tab/copy state.
 */

export type TabKey = 'cloudformation' | 'terraform' | 'cli' | 'cloudshell'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'cloudformation', label: 'CloudFormation' },
  { key: 'terraform', label: 'Terraform' },
  { key: 'cli', label: 'AWS CLI' },
  { key: 'cloudshell', label: 'CloudShell' },
]

interface Props {
  triggerLabel: string
  modalTitle: string
  intro: ReactNode
  footer: ReactNode
  snippets: Record<TabKey, string>
  /** Namespaces the data-testids (e.g. "aws-iam-help" → "aws-iam-help-modal"). */
  testIdPrefix: string
  defaultTab?: TabKey
}

export function IamSetupTabs({
  triggerLabel,
  modalTitle,
  intro,
  footer,
  snippets,
  testIdPrefix,
  defaultTab = 'cloudformation',
}: Props) {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<TabKey>(defaultTab)
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippets[tab])
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard blocked (e.g. insecure context) — no-op */
    }
  }

  return (
    <div style={{ marginTop: '0.75rem' }} data-testid={testIdPrefix}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testIdPrefix}-toggle`}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--accent)',
          cursor: 'pointer',
          padding: 0,
          fontSize: '0.9rem',
          textDecoration: 'underline',
        }}
      >
        {triggerLabel}
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={modalTitle}
        ariaLabel={modalTitle}
        testId={`${testIdPrefix}-modal`}
        maxWidth={840}
      >
        <p style={{ margin: '0 0 0.85rem', fontSize: '0.88rem', color: 'var(--muted)', lineHeight: 1.5 }}>
          {intro}
        </p>

        <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`${testIdPrefix}-tab-${t.key}`}
                aria-selected={tab === t.key}
                style={{
                  border: 'none',
                  borderBottom: tab === t.key ? '2px solid var(--accent)' : '2px solid transparent',
                  background: 'none',
                  padding: '0.5rem 0.9rem',
                  cursor: 'pointer',
                  fontWeight: tab === t.key ? 600 : 400,
                  color: tab === t.key ? 'var(--accent)' : 'inherit',
                }}
              >
                {t.label}
              </button>
            ))}
            <button
              type="button"
              onClick={copy}
              data-testid={`${testIdPrefix}-copy`}
              style={{ marginLeft: 'auto', border: 'none', background: 'none', padding: '0.5rem 0.9rem', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--muted)' }}
            >
              {copied ? 'Copied ✓' : 'Copy'}
            </button>
          </div>

          <pre
            data-testid={`${testIdPrefix}-snippet`}
            style={{ margin: 0, padding: '0.85rem 1rem', overflowX: 'auto', fontSize: '0.78rem', lineHeight: 1.45, background: 'var(--surface)' }}
          >
            <code>{snippets[tab]}</code>
          </pre>
        </div>

        <p style={{ margin: '0.85rem 0 0', fontSize: '0.82rem', color: 'var(--muted)', lineHeight: 1.5 }}>
          {footer}
        </p>
      </Modal>
    </div>
  )
}
